<template>
  <!-- Modal style for success alerts (like inquiry popup) -->
  <div v-if="alertType && alertMessage && alertType === 'alert-success'" class="custom-modal-overlay" @click.self="clearAlert">
    <div class="custom-modal">
      <div class="custom-modal-header success">
        <h4 class="custom-modal-title">
          <span>✓</span>
          {{ getSuccessTitle() }}
        </h4>
        <button class="custom-modal-close" @click="clearAlert">&times;</button>
      </div>
      <div class="custom-modal-body">
        <p>{{ alertMessage }}</p>
      </div>
      <div class="custom-modal-footer">
        <button class="btn btn-modal btn-modal-success" @click="clearAlert">OK</button>
      </div>
    </div>
  </div>
  
  <!-- Original style for other alert types -->
  <div v-else-if="alertType && alertMessage" class="alert-container">
    <div :class="['alert', 'alert-dismissible', 'fade', 'show', alertType]" role="alert">
      <div class="alert-content">
        <i :class="alertIcon" aria-hidden="true"></i>
        <span>{{ alertMessage }}</span>
      </div>
      <button type="button" class="btn-close" @click="clearAlert" aria-label="Close">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'Alert',
  computed: {
    ...mapState('alert', ['type', 'output']),
    alertType() {
      return this.type;
    },
    alertMessage() {
      return this.output;
    },
    alertIcon() {
      switch (this.type) {
        case 'alert-success':
          return 'fas fa-check-circle';
        case 'alert-danger':
          return 'fas fa-exclamation-circle';
        case 'alert-warning':
          return 'fas fa-exclamation-triangle';
        case 'alert-info':
          return 'fas fa-info-circle';
        default:
          return 'fas fa-info-circle';
      }
    }
  },
  methods: {
    ...mapActions('alert', ['clear']),
    clearAlert() {
      this.clear();
    },
    getSuccessTitle() {
      // Check if message is about reservation
      const message = this.alertMessage || '';
      if (message.toLowerCase().includes('reserva') || message.toLowerCase().includes('reservation')) {
        return this.$t('reservation.confirmedSuccess') || 'Reserva Confirmada com Sucesso!';
      }
      // Default success title
      return 'Sucesso';
    }
  },
  data() {
    return {
      successTimeout: null
    };
  },
  mounted() {
    // Auto-hide success alert after 3.5 seconds
    if (this.alertType && this.alertMessage && this.alertType === 'alert-success') {
      this.successTimeout = setTimeout(() => {
        this.clearAlert();
      }, 3500);
    } else if (this.alertType && this.alertMessage) {
      // Auto-hide other alerts after 5 seconds
      setTimeout(() => {
        this.clearAlert();
      }, 5000);
    }
  },
  beforeDestroy() {
    // Clear timeout if component is destroyed
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
  },
  watch: {
    alertType() {
      // Clear existing timeout
      if (this.successTimeout) {
        clearTimeout(this.successTimeout);
        this.successTimeout = null;
      }
      
      // Auto-hide success alert after 3.5 seconds
      if (this.alertType && this.alertMessage && this.alertType === 'alert-success') {
        this.successTimeout = setTimeout(() => {
          this.clearAlert();
        }, 3500);
      } else if (this.alertType && this.alertMessage) {
        // Auto-hide other alerts after 5 seconds
        setTimeout(() => {
          this.clearAlert();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
/* Custom Modal Styles (for success alerts - like inquiry popup) */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.custom-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

.custom-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.custom-modal-header.success {
  background-color: var(--primary-color, #B1D1A0);
  color: white;
  border-radius: 8px 8px 0 0;
}

.custom-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.custom-modal-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: inherit;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.custom-modal-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.custom-modal-body {
  padding: 1.5rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #333;
}

.custom-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.btn-modal {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-modal:hover {
  background-color: #0056b3;
}

.btn-modal-success {
  background-color: var(--primary-color, #B1D1A0) !important;
  border-color: var(--primary-color, #B1D1A0) !important;
  color: white !important;
}

.btn-modal-success:hover {
  background-color: #9FC08F !important;
  border-color: #9FC08F !important;
  color: white !important;
  opacity: 0.9;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Original Alert Styles (for non-success alerts) */
.alert-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  max-width: 500px;
  min-width: 300px;
  width: 90%;
}

.alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.alert-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.alert-content i {
  margin-right: 0.5rem;
  font-size: 1.1rem;
}

.alert-success {
  color: #0f5132;
  background-color: #d1e7dd;
  border-color: #badbcc;
}

.alert-danger {
  color: #842029;
  background-color: #f8d7da;
  border-color: #f5c2c7;
}

.alert-warning {
  color: #664d03;
  background-color: #fff3cd;
  border-color: #ffecb5;
}

.alert-info {
  color: #055160;
  background-color: #d1ecf1;
  border-color: #b6effb;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
  opacity: 0.5;
  transition: opacity 0.15s ease-in-out;
}

.btn-close:hover {
  opacity: 0.75;
}

.fade {
  transition: opacity 0.15s linear;
}

.show {
  opacity: 1;
}

@media (max-width: 768px) {
  .alert-container {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    right: auto;
    max-width: 90%;
    min-width: 280px;
    width: 90%;
  }
  
  .custom-modal {
    width: 95%;
  }
}
</style>

